import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  IconButton,
} from '@mui/material';
import {
  AccountBalance,
  CreditCard,
  Receipt,
  Info,
  ContentCopy,
  Verified,
} from '@mui/icons-material';

// Mock Thaklis bank accounts - TODO: Replace with API call to GET /v1/bank-accounts/customer/available
const thaklisBankAccounts = [
  {
    id: 'bank-001',
    accountName: 'Thaklis - ENBD Main Account',
    bankName: 'Emirates NBD',
    accountNumber: '****7890',
    iban: 'AE070331234567890123456',
    currency: 'AED',
    isActive: true,
    isPrimary: true,
  },
  {
    id: 'bank-002',
    accountName: 'Thaklis - ADCB Business Account',
    bankName: 'Abu Dhabi Commercial Bank',
    accountNumber: '****3456',
    iban: 'AE330321987654321098765',
    currency: 'AED',
    isActive: true,
    isPrimary: false,
  },
  {
    id: 'bank-003',
    accountName: 'Thaklis - DIB Shariah Account',
    bankName: 'Dubai Islamic Bank',
    accountNumber: '****2345',
    iban: 'AE440401234567890123456',
    currency: 'AED',
    isActive: true,
    isPrimary: false,
  },
];

const ConfirmationStep = ({ formData, onBankAccountSelect }) => {
  const [selectedDestinationBank, setSelectedDestinationBank] = useState(
    formData.selectedBankAccount?.id || (formData.paymentType === 'OFFLINE' ? thaklisBankAccounts[0]?.id : null)
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatIBAN = (iban) => {
    return iban.match(/.{1,4}/g)?.join(' ') || iban;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // TODO: Add toast notification
  };

  const isGatewayPayment = formData.paymentType === 'GATEWAY';
  const isOfflinePayment = formData.paymentType === 'OFFLINE';

  // Get the currently selected destination bank account details
  const getSelectedBankAccount = () => {
    return thaklisBankAccounts.find(acc => acc.id === selectedDestinationBank);
  };

  const handleBankSelection = (bankId) => {
    setSelectedDestinationBank(bankId);
    const selectedBank = thaklisBankAccounts.find(acc => acc.id === bankId);
    if (onBankAccountSelect) {
      onBankAccountSelect(selectedBank);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
        Review & Confirm
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please review your details before submitting
      </Typography>

      {/* Amount Card */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <CardContent>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {isGatewayPayment ? 'Wallet Top-up (you receive)' : 'Topup Amount'}
          </Typography>
          <Typography variant="h3" fontWeight={700} sx={{ my: 1 }}>
            {formatCurrency(parseFloat(formData.amount))}
          </Typography>
          {isGatewayPayment && formData.gatewayFee > 0 && (
            <Box>
              <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Platform Fee:
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="body2">
                    + {formatCurrency(formData.gatewayFee)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight={600}>
                    Total to Pay:
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(
                      formData.totalToPay != null
                        ? formData.totalToPay
                        : parseFloat(formData.amount) + (formData.gatewayFee || 0)
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 1 }}>
                The platform fee is charged by the gateway on top of your top-up.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Payment Details Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Payment Details
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={5}>
              <Typography variant="body2" color="text.secondary">
                Payment Method:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isGatewayPayment ? <CreditCard fontSize="small" /> : <AccountBalance fontSize="small" />}
                <Typography variant="body2" fontWeight={600}>
                  {isGatewayPayment ? 'Online Payment Gateway' : 'Bank Transfer / Cash Deposit'}
                </Typography>
              </Box>
            </Grid>

            {isGatewayPayment && formData.selectedGateway && (
              <>
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">
                    Gateway:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" fontWeight={600}>
                    {formData.selectedGateway.gatewayName}
                  </Typography>
                  <Chip
                    label={formData.selectedGateway.gatewayType}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
              </>
            )}

            {isOfflinePayment && (
              <>
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">
                    Method:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" fontWeight={600}>
                    {formData.paymentMethod === 'BANK_TRANSFER' ? 'Bank Transfer' : 'Cash Deposit'}
                  </Typography>
                </Grid>

                {formData.selectedBankAccount && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="body2" color="text.secondary">
                        Reference Number:
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={600}>
                        {formData.referenceNumber}
                      </Typography>
                    </Grid>

                    <Grid item xs={5}>
                      <Typography variant="body2" color="text.secondary">
                        Receipt:
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Receipt fontSize="small" color="success" />
                        <Typography variant="body2" fontWeight={600}>
                          {formData.receipt?.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {(formData.receipt?.size / 1024).toFixed(2)} KB
                      </Typography>
                    </Grid>

                    {formData.customerNotes && (
                      <>
                        <Grid item xs={5}>
                          <Typography variant="body2" color="text.secondary">
                            Notes:
                          </Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="body2">
                            {formData.customerNotes}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Destination Bank Selection (FOR OFFLINE PAYMENTS) */}
      {isOfflinePayment && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalance sx={{ color: 'primary.main' }} />
              Select Destination Bank Account
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which Thaklis bank account should receive your payment
            </Typography>
            <Divider sx={{ my: 2 }} />

            {/* Bank Selection Radio Group */}
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedDestinationBank}
                onChange={(e) => handleBankSelection(e.target.value)}
              >
                {thaklisBankAccounts.filter(acc => acc.isActive).map((account) => {
                  const isSelected = selectedDestinationBank === account.id;
                  return (
                    <Box
                      key={account.id}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        bgcolor: isSelected ? 'action.hover' : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        }
                      }}
                      onClick={() => handleBankSelection(account.id)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <FormControlLabel
                            value={account.id}
                            control={<Radio />}
                            label={
                              <Box sx={{ ml: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {account.bankName}
                                  </Typography>
                                  {account.isPrimary && (
                                    <Chip label="Primary" size="small" color="primary" />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                  {account.accountName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Account: {account.accountNumber}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                                  <Typography variant="caption" fontFamily="monospace">
                                    {formatIBAN(account.iban)}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(account.iban);
                                    }}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            }
                            sx={{ width: '100%', m: 0 }}
                          />
                        </Box>
                        {isSelected && (
                          <Verified color="primary" sx={{ mt: 1 }} />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </RadioGroup>
            </FormControl>

            {/* Selected Bank Summary */}
            {getSelectedBankAccount() && (
              <Paper sx={{ mt: 3, p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'primary.main' }}>
                  ✓ Payment will be sent to:
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {getSelectedBankAccount().bankName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {getSelectedBankAccount().accountName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Account #: {getSelectedBankAccount().accountNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Amount: {formatCurrency(parseFloat(formData.amount))}
                  </Typography>
                </Box>
              </Paper>
            )}
          </CardContent>
        </Card>
      )}

      {/* Important Notice */}
      {isGatewayPayment && (
        <Alert severity="info" icon={<Info />}>
          <AlertTitle>Next Step</AlertTitle>
          After clicking "Submit Request", you will be redirected to {formData.selectedGateway?.gatewayName} to complete the payment securely.
          Your wallet will be topped up instantly after successful payment.
        </Alert>
      )}

      {isOfflinePayment && (
        <Alert severity="warning" icon={<Info />}>
          <AlertTitle>Approval Required</AlertTitle>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            <li>Your request will be reviewed by our finance team</li>
            <li>Processing time: 1-2 business days</li>
            <li>You'll receive an email notification when approved</li>
            <li>Amount will be added to your wallet after approval</li>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default ConfirmationStep;
